import { DialogDisclosure, Form, FormSubmit, Heading } from "@ariakit/react";
import {
  DialogTrigger,
  LetterheadTextField,
  MiddotSeparated,
  SafariCheck,
  useForm,
} from "@indietabletop/appkit";
import { useCallback, type ReactNode } from "react";
import { Link } from "wouter";
import { href } from "../App/href";
import { database, useQuery } from "../database/database";
import {
  DialogContent,
  DialogHeader,
  ScrollableDialog,
} from "../Dialog/Dialog";
import { latest } from "../engine/latest";
import { Warband } from "../engine/Warband";
import { FailureView } from "../FailureView/FailureView";
import { LoadingView } from "../LoadingView/LoadingView";
import { Page } from "../oss/Layout/Layout";
import { ProceedIcon } from "../PageHeaderButton/Icon";
import { empty, grid, item, submit, view } from "./style.css";
import { useHandleSuccess } from "./useHandleSuccess";

function GridListView(props: { children: ReactNode; fallback?: ReactNode }) {
  return (
    <>
      <ul className={grid.container}>{props.children}</ul>
      {props.fallback && <div className={grid.fallback}>{props.fallback}</div>}
    </>
  );
}

function GridListItem(props: {
  href: string;
  heading: string;
  summary: ReactNode;
}) {
  const { href, heading, summary } = props;
  return (
    <li className={item.fragment}>
      <Link href={href} className={item.container} transition>
        <div className={item.heading}>{heading}</div>
        <div className={item.summary}>{summary}</div>
      </Link>
    </li>
  );
}

function SubmitChoice(props: {
  label: string;
  description: ReactNode;
  disabled?: boolean;
}) {
  const { label, description, disabled } = props;

  return (
    <FormSubmit disabled={disabled} className={submit.container}>
      <div>
        <div className={submit.label}>{label}</div>

        <div className={submit.description}>{description}</div>
      </div>

      <div className={submit.icon}>
        <ProceedIcon />
      </div>
    </FormSubmit>
  );
}

function CreateRecordDialog() {
  const handleSuccess = useHandleSuccess();
  const { form } = useForm({
    defaultValues: { name: "" },
    async onSubmit({ values }) {
      const item = Warband.from(latest, { name: values.name });
      return await database.warbands.putOne(item.data);
    },
    onSuccess(id) {
      handleSuccess({
        navigateTo: href("~/builder/w/:warbandId", { path: { warbandId: id } }),
      });
    },
  });

  return (
    <ScrollableDialog>
      <Form store={form} autoComplete="off" noValidate>
        <DialogHeader heading="Create..." />

        <DialogContent
          style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}
        >
          <LetterheadTextField
            name={form.names.name}
            label="Name"
            autoFocus
            placeholder="E.g. Vanguard of the Silver Chalice"
            data-1p-ignore="true"
          />

          <div
            style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}
          >
            <SubmitChoice
              label="Warband"
              description={
                <>
                  Gather 6 knights for a <em>Quick Battle</em>.
                </>
              }
            />

            <SubmitChoice
              label="Knightly Order"
              disabled
              description={
                <>
                  Track named knights during <em>Campaign Play</em>.
                </>
              }
            />
          </div>
        </DialogContent>
      </Form>
    </ScrollableDialog>
  );
}

function KnightlyOrdersView(props: { knightlyOrders: Warband[] }) {
  const { knightlyOrders } = props;

  return (
    <SafariCheck performCheck={knightlyOrders.length === 0}>
      <div className={view.container}>
        <div className={view.content}>
          <GridListView
            fallback={
              <div className={empty.container}>
                <Heading className={empty.heading}>Nothing here yet...</Heading>
                <p className={empty.hint}>
                  Use the button below to get started.
                </p>
              </div>
            }
          >
            {knightlyOrders.map((item) => {
              return (
                <GridListItem
                  key={item.id}
                  href={href("~/builder/w/:warbandId", {
                    path: { warbandId: item.id },
                  })}
                  heading={item.name}
                  summary={
                    <MiddotSeparated>
                      <em>Quick Battle</em>
                      {item.summary}
                    </MiddotSeparated>
                  }
                />
              );
            })}
          </GridListView>
        </div>

        <DialogTrigger>
          <CreateRecordDialog />
          <DialogDisclosure className={view.action}>Create...</DialogDisclosure>
        </DialogTrigger>
      </div>
    </SafariCheck>
  );
}

export function KnightlyOrdersPage() {
  const result = useQuery(
    useCallback((db) => db.warbands.getAll(), []),
    "records",
  );

  if (result.isPending) {
    return (
      <Page backHref="~/">
        <LoadingView />
      </Page>
    );
  }

  if (result.isFailure) {
    return (
      <Page backHref="~/">
        <FailureView failure={result.failure} />
      </Page>
    );
  }

  const { value: knightlyOrders } = result.mapSuccess((arr) =>
    arr.map((data) => new Warband(latest, data)),
  );

  return (
    <Page title="Builder" backHref="~/">
      <KnightlyOrdersView knightlyOrders={knightlyOrders} />
    </Page>
  );
}
